/**
 * Tiny breadcrumb component for nested pages.
 *
 * Usage:
 *   <Breadcrumb items={[
 *     { label: "Admin", to: "/admin" },
 *     { label: "Templates" },           // current page, no `to` → not a link
 *   ]} />
 *
 * Designed to sit *above* the page H1. Small, low-contrast, tracking-wide
 * so it doesn't compete with the heading.
 */
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export default function Breadcrumb({ items = [] }) {
  if (!items.length) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2"
      data-testid="breadcrumb"
    >
      {items.map((it, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={`${it.label}-${i}`} className="inline-flex items-center gap-1">
            {it.to && !isLast ? (
              <Link
                to={it.to}
                className="hover:text-slate-200 transition-colors"
                data-testid={`breadcrumb-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {it.label}
              </Link>
            ) : (
              <span
                className={isLast ? "text-slate-300" : ""}
                aria-current={isLast ? "page" : undefined}
                data-testid={isLast ? "breadcrumb-current" : undefined}
              >
                {it.label}
              </span>
            )}
            {!isLast && <ChevronRight className="w-3 h-3 text-slate-600" />}
          </span>
        );
      })}
    </nav>
  );
}
